import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from '@openng/optimus-ui/card';
import { TableModule } from '@openng/optimus-ui/table';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TagModule } from '@openng/optimus-ui/tag';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { AutoCompleteModule, AutoCompleteCompleteEvent } from '@openng/optimus-ui/autocomplete';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { ToastModule } from '@openng/optimus-ui/toast';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { MessageService } from '@openng/optimus-ui/api';
import {
  FormsModule,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { OrganizationsService } from '../../services/organizations.service';
import { UiStateService } from '../../services/ui-state.service';
import { PeriodOption } from '../../models/dashboard.types';
import {
  OrganizationSummary,
  OrgFunnelSummary,
  CompanySuggestion,
  CreatedOrganization,
} from '../../models/organization.types';
import { SearchUser } from '../../models/search.types';
import { DateRangeSelectorComponent, DateRange } from '../shared/date-range-selector.component';
import { debounceTime, Subject, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorBannerComponent } from '../shared/error-banner.component';
import { MessageModule } from '@openng/optimus-ui/message';
import { CreateOrganizationDialogComponent } from './create-organization-dialog.component';

@Component({
  selector: 'app-organizations',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CardModule,
    TableModule,
    ButtonModule,
    TagModule,
    InputTextModule,
    AutoCompleteModule,
    DialogModule,
    TabsModule,
    ToastModule,
    TooltipModule,
    FormsModule,
    ReactiveFormsModule,
    DateRangeSelectorComponent,
    ErrorBannerComponent,
    MessageModule,
    CreateOrganizationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './organizations.component.html',
  styleUrl: './organizations.component.scss',
})
export class OrganizationsComponent {
  protected orgService: OrganizationsService = inject(OrganizationsService);
  protected uiState = inject(UiStateService);
  private router = inject(Router);
  private messageService = inject(MessageService);

  protected selectedPeriod = this.uiState.selectedPeriod;
  protected startDate = this.uiState.startDate;
  protected endDate = this.uiState.endDate;

  protected showCreateDialog = signal(false);
  protected createNotice = signal<{ severity: 'success' | 'warn'; text: string } | null>(null);
  /** The list is the working view; the aggregate funnel is the secondary read-only one. */
  protected activeTab = signal<'list' | 'funnel'>('list');

  protected orgSearch = signal('');
  /** Row offset of the table paginator. Reset on search: PrimeNG only steps back one page when
   * totalRecords shrinks, so a search made from page 5 would keep requesting an empty page. */
  protected firstRow = signal(0);
  protected userSearch = signal<SearchUser | null>(null);
  protected userResults = signal<SearchUser[]>([]);
  protected userSearchLoading = signal(false);
  private orgSearchSubject = new Subject<string>();
  private userSearchSubject = new Subject<string>();

  protected funnelSteps = [
    { key: 'invited', label: 'Invited', shortLabel: 'INV', color: '#8B5CF6' },
    { key: 'registered', label: 'Registered', shortLabel: 'REG', color: '#6366F1' },
    { key: 'loggedIn', label: 'Logged In', shortLabel: 'Login', color: '#3B82F6' },
    {
      key: 'onboardingDiscoveryStarted',
      label: 'Discovery Started',
      shortLabel: 'Disc.S',
      color: '#0EA5E9',
    },
    {
      key: 'onboardingDiscoveryCompleted',
      label: 'Discovery Done',
      shortLabel: 'Disc.D',
      color: '#06B6D4',
    },
    {
      key: 'onboardingStepStarted',
      label: 'Steps Started',
      shortLabel: 'Step.S',
      color: '#14B8A6',
    },
    { key: 'onboardingStepCompleted', label: 'Steps Done', shortLabel: 'Step.D', color: '#10B981' },
    {
      key: 'onboardingFieldAssistUsed',
      label: 'Field Assist Used',
      shortLabel: 'FA',
      color: '#22C55E',
    },
    { key: 'onboardingCompleted', label: 'Onboarding Done', shortLabel: 'Onb.D', color: '#84CC16' },
    {
      key: 'onboardingConfidenceReviewed',
      label: 'Confidence Reviewed',
      shortLabel: 'Conf',
      color: '#EAB308',
    },
    { key: 'playbookCreated', label: 'Playbook Created', shortLabel: 'PB', color: '#F59E0B' },
    { key: 'documentsCreated', label: 'Documents Created', shortLabel: 'Docs', color: '#F97316' },
    {
      key: 'liveAssistantUsed',
      label: 'Live Assistant Used',
      shortLabel: 'Live',
      color: '#EF4444',
    },
  ];

  protected aggregateFunnel = computed<OrgFunnelSummary | null>(() => {
    const orgs = this.orgService.organizations();
    if (!orgs || orgs.length === 0) return null;

    const aggregate: OrgFunnelSummary = {
      totalUsers: 0,
      invited: 0,
      registered: 0,
      loggedIn: 0,
      onboardingDiscoveryStarted: 0,
      onboardingDiscoveryCompleted: 0,
      onboardingStepStarted: 0,
      onboardingStepCompleted: 0,
      onboardingFieldAssistUsed: 0,
      onboardingCompleted: 0,
      onboardingConfidenceReviewed: 0,
      playbookCreated: 0,
      documentsCreated: 0,
      liveAssistantUsed: 0,
      playbookOptional: false,
    };

    for (const org of orgs) {
      if (!org.funnelSummary || org.isInternal) continue;
      aggregate.totalUsers += org.funnelSummary.totalUsers;
      aggregate.invited += org.funnelSummary.invited;
      aggregate.registered += org.funnelSummary.registered;
      aggregate.loggedIn += org.funnelSummary.loggedIn;
      aggregate.onboardingDiscoveryStarted += org.funnelSummary.onboardingDiscoveryStarted;
      aggregate.onboardingDiscoveryCompleted += org.funnelSummary.onboardingDiscoveryCompleted;
      aggregate.onboardingStepStarted += org.funnelSummary.onboardingStepStarted;
      aggregate.onboardingStepCompleted += org.funnelSummary.onboardingStepCompleted;
      aggregate.onboardingFieldAssistUsed += org.funnelSummary.onboardingFieldAssistUsed;
      aggregate.onboardingCompleted += org.funnelSummary.onboardingCompleted;
      aggregate.onboardingConfidenceReviewed += org.funnelSummary.onboardingConfidenceReviewed;
      aggregate.playbookCreated += org.funnelSummary.playbookCreated;
      aggregate.documentsCreated += org.funnelSummary.documentsCreated;
      aggregate.liveAssistantUsed += org.funnelSummary.liveAssistantUsed;
    }

    return aggregate;
  });

  constructor() {
    effect(() => {
      this.uiState.startDate();
      this.uiState.endDate();
      untracked(() => this.loadData());
    });

    this.orgSearchSubject.pipe(debounceTime(400), takeUntilDestroyed()).subscribe(() => {
      this.firstRow.set(0);
      this.loadData();
    });
    this.userSearchSubject
      .pipe(
        debounceTime(400),
        switchMap((term) => {
          const q = term.trim();
          if (!q) {
            this.userResults.set([]);
            return of<SearchUser[]>([]);
          }
          this.userSearchLoading.set(true);
          return this.orgService.searchUsers(q).pipe(
            catchError(() => {
              this.userResults.set([]);
              return of<SearchUser[]>([]);
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((results) => {
        this.userResults.set(results);
        this.userSearchLoading.set(false);
      });

    this.companyQuery
      .pipe(
        // catchError inside the inner observable: an error reaching the outer stream would
        // terminate it and kill autocomplete for the rest of the session.
        switchMap((query) =>
          this.orgService
            .autocompleteCompany(query)
            .pipe(catchError(() => of<CompanySuggestion[]>([]))),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((suggestions) => this.companySuggestions.set(suggestions));
  }

  loadData(event?: { first?: number | null; rows?: number | null } | undefined) {
    const { startDate, endDate } = this.uiState.getRange();
    const rows = event?.rows ?? 10;
    const first = event?.first ?? this.firstRow();
    const page = Math.floor(first / rows) + 1;
    this.orgService.loadOrganizations(startDate, endDate, page, rows, this.orgSearch());
  }

  onPeriodChange(period: PeriodOption) {
    this.uiState.setPeriod(period);
  }

  onDateRangeChange(dateRange: DateRange) {
    this.uiState.setRange(dateRange.startDate, dateRange.endDate);
  }

  onOrgSearchInput() {
    this.orgSearchSubject.next(this.orgSearch());
  }

  /**
   * An organisation created here is not in the current page of the table, and it has no usage in
   * the selected period, so reloading is what makes it visible at all. The notice carries the plan
   * because that is the part an operator gets wrong, and warns when the invite mail did not go out:
   * the organisation exists either way, but nobody has a link to claim it.
   */
  onOrganizationCreated(organisation: CreatedOrganization) {
    const plan = organisation.subscriptions[0]?.pricingPlan?.name;
    const created = `${organisation.name} created${plan ? ` on ${plan}` : ''}.`;

    this.createNotice.set(
      organisation.inviteEmailSent
        ? { severity: 'success', text: `${created} Invite sent to the admin.` }
        : {
            severity: 'warn',
            text: `${created} The invite email failed to send - resend it before the call.`,
          },
    );

    this.loadData();
  }

  openDetail(org: OrganizationSummary) {
    this.router.navigate(['/organizations', org.id]);
  }

  onUserSearchInput(query: string) {
    this.userSearchSubject.next(query);
  }

  onUserSelected(user: SearchUser) {
    this.userSearch.set(null);
    this.userResults.set([]);
    if (user.organisationId) {
      this.router.navigate(['/organizations', user.organisationId], {
        queryParams: { highlightUser: user.id },
      });
    }
  }

  getFunnelCount(summary: OrgFunnelSummary, key: string): number {
    return (summary as unknown as Record<string, number>)[key] ?? 0;
  }

  getFunnelPercentage(summary: OrgFunnelSummary, key: string): number {
    if (!summary.totalUsers) return 0;
    const count = this.getFunnelCount(summary, key);
    return Math.round((count / summary.totalUsers) * 100);
  }

  getStatusSeverity(
    status: string,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'TRIALING':
        return 'info';
      case 'PAST_DUE':
        return 'warn';
      case 'CANCELED':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  // --- Invite organisation ---------------------------------------------------

  protected showInviteDialog = signal(false);
  protected companySuggestions = signal<CompanySuggestion[]>([]);
  protected companySelected = signal(false);

  protected inviteForm = new FormGroup({
    name: new FormControl<string | CompanySuggestion | null>(null, Validators.required),
    adminEmail: new FormControl('', [Validators.required, Validators.email]),
    registrationCode: new FormControl(''),
    address: new FormControl(''),
  });

  // The lookup proxies an external company register, so a slow reply for "ac" can land
  // after "acme". switchMap drops the earlier request instead of letting it overwrite.
  private companyQuery = new Subject<string>();

  openInviteDialog() {
    this.inviteForm.reset();
    this.inviteForm.controls.name.enable();
    this.companySelected.set(false);
    this.companySuggestions.set([]);
    this.showInviteDialog.set(true);
  }

  searchCompany(event: AutoCompleteCompleteEvent) {
    this.companyQuery.next(event.query);
  }

  /** Picking a registry hit locks the name and fills in what the registry knows. */
  onCompanySelect(event: { value: CompanySuggestion | string }) {
    if (typeof event.value !== 'object') return;
    this.inviteForm.patchValue({
      name: event.value.name,
      registrationCode: event.value.registrationCode ?? '',
      address: event.value.address ?? '',
    });
    this.inviteForm.controls.name.disable();
    this.companySelected.set(true);
  }

  clearCompanySelection() {
    this.companySelected.set(false);
    this.inviteForm.patchValue({ name: '', registrationCode: '', address: '' });
    this.inviteForm.controls.name.enable();
    this.companySuggestions.set([]);
  }

  sendInvite() {
    if (this.inviteForm.invalid) return;
    const { name, adminEmail, registrationCode, address } = this.inviteForm.getRawValue();

    this.orgService
      .inviteOrganisation({
        name: typeof name === 'string' ? name : (name?.name ?? ''),
        adminEmail: adminEmail ?? '',
        registrationCode: registrationCode ?? '',
        address: address ?? '',
      })
      .subscribe({
        next: () => {
          // Closed here, not before subscribe: a duplicate org name errors and the
          // user would otherwise lose every field they typed.
          this.showInviteDialog.set(false);
          this.messageService.add({
            severity: 'success',
            summary: `Invite sent to ${adminEmail}`,
            life: 3000,
          });
          this.loadData();
        },
        error: (e) =>
          this.messageService.add({
            severity: 'error',
            summary: e.error?.error ?? 'Invite failed',
            detail: e.error?.message,
            life: 5000,
          }),
      });
  }

  /**
   * Open the organisation so it can be torn down there.
   *
   * A confirmation dialog on a list of every organisation asks "are you sure?" when the question
   * that matters is "is this the row you meant?" - which only the detail screen, with the blast
   * radius and the type-the-name gate, can actually put to an operator.
   */
  openTeardown(event: Event, org: OrganizationSummary) {
    event.stopPropagation();
    this.openDetail(org);
  }
}
